import { slugify } from '@/utils/string';
import clsx from 'clsx';
import { AlertTriangle } from 'lucide-react';
import React from 'react';

const inputBaseClasses =
  'w-full h-[50px] rounded-[10px] border-[1.5px] bg-lighter px-3.5 text-[15px] text-textDark outline-none transition-colors placeholder:text-grey10';

export type AppTextInputStatus = 'default' | 'active' | 'error' | 'warn';

export interface AppTextInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'className'> {
  label?: React.ReactNode;
  /** Shows a required-marker (*) next to the label. */
  required?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  /** Static leading text adornment, e.g. a currency symbol ("$"). */
  prefix?: string;
  /** Static trailing text adornment, e.g. a unit label ("gallons"). */
  suffix?: string;
  /** Visual state of the field. Defaults to inferring from `error`/`warning`/`disabled` when omitted. */
  status?: AppTextInputStatus;
  error?: string;
  warning?: string;
  hint?: string;
  inputClassName?: string;
}

const AppTextInput = React.forwardRef<HTMLInputElement, AppTextInputProps>(
  (
    {
      label,
      required,
      leftIcon,
      rightIcon,
      prefix,
      suffix,
      status,
      error,
      warning,
      hint,
      inputClassName = '',
      id,
      ...inputProps
    },
    ref
  ) => {
    const inputId =
      id ?? (typeof label === 'string' ? `input-${slugify(label)}` : undefined);
    const hasLeftIcon = Boolean(leftIcon);
    const hasRightIcon = Boolean(rightIcon);
    const hasPrefix = Boolean(prefix);
    const hasSuffix = Boolean(suffix);
    const isDisabled = inputProps.disabled;

    const resolvedStatus: AppTextInputStatus =
      status ?? (error ? 'error' : warning ? 'warn' : 'default');
    const isActive = resolvedStatus === 'active';

    const borderClass = isDisabled
      ? 'border-grey2'
      : isActive
        ? 'border-electric'
        : resolvedStatus === 'warn'
          ? 'border-warning'
          : 'border-cloud';

    const bgClass = isDisabled ? 'bg-grey4' : isActive ? 'bg-cloud' : '';

    const messageId = error
      ? `${inputId}-error`
      : warning
        ? `${inputId}-warning`
        : undefined;

    return (
      <div>
        {label && (
          <label
            htmlFor={inputId}
            className="mb-1.5 flex items-center gap-1.5 text-sm font-medium text-textDark"
          >
            {label}
            {required && <span className="font-bold text-danger">*</span>}
          </label>
        )}
        <div className="relative flex items-center">
          {leftIcon && (
            <div className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-grey10 [&>svg]:w-5 [&>svg]:h-5">
              {leftIcon}
            </div>
          )}
          {prefix && (
            <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-sm font-medium text-textDark">
              {prefix}
            </span>
          )}
          <input
            ref={ref}
            id={inputId}
            className={clsx(
              inputBaseClasses,
              hasLeftIcon && 'pl-10',
              hasPrefix && 'pl-7',
              hasRightIcon && 'pr-11',
              hasSuffix && 'pr-[66px]',
              borderClass,
              bgClass,
              !isActive && !isDisabled && 'focus:bg-cloud focus:border-primary',
              isDisabled && 'border-grey2 text-grey1 cursor-not-allowed',
              inputClassName
            )}
            disabled={isDisabled}
            aria-invalid={Boolean(error)}
            aria-describedby={messageId}
            {...inputProps}
          />
          {rightIcon && (
            <div className="absolute right-3.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-textLight [&>svg]:w-5 [&>svg]:h-5">
              {rightIcon}
            </div>
          )}
          {suffix && (
            <span className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-sm font-medium text-grey10">
              {suffix}
            </span>
          )}
        </div>
        {error ? (
          <p id={messageId} className="mt-1 text-xs text-danger">
            {error}
          </p>
        ) : warning ? (
          <p
            id={messageId}
            className="mt-1 flex items-center gap-1 text-xs text-warning"
          >
            <AlertTriangle className="size-4 shrink-0" aria-hidden />
            {warning}
          </p>
        ) : hint ? (
          <p className="mt-1 text-[11.5px] leading-snug text-grey10">{hint}</p>
        ) : null}
      </div>
    );
  }
);

AppTextInput.displayName = 'AppTextInput';

export default AppTextInput;
